import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { orderService } from "../services/order";
import { productService } from "../services/product"; // To select products for order items
import { formatDateForPortugal, formatDateForInput } from "../utils/dateUtils";
import { extractPackMultiplier, getUnitWeight } from "../utils/packUtils";

const OrderDetails = () => {
  const [orderData, setOrderData] = useState({
    order_number: "",
    client_name: "",
    client_nif: "",
    delivery_date: "",
    total_cheese_count: 0,
    total_weight_kg: 0,
    delivery_status: "pending",
    destination_address: "",
    recipient_name: "",
    packing_details: "",
    items: [],
  });

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelingWeekly, setCancelingWeekly] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  // Função auxiliar para calcular as unidades a partir do peso total e peso por unidade
  const calculateUnits = (item, productData) => {
    if (!productData || item.ordered_quantity === undefined || item.ordered_quantity === null)
      return 0;

    const productWeightPerUnit = productData.is_cured
      ? productData.cured_weight_kg
      : productData.initial_weight_kg;

    if (
      productWeightPerUnit === undefined ||
      productWeightPerUnit === null ||
      productWeightPerUnit === 0
    )
      return 0;

    // Calcular unidades reais considerando o peso unitário
    const realUnits = item.ordered_quantity / productWeightPerUnit;

    // Retornar o número total de unidades (considerando o multiplicador do pack)
    const packMultiplier = extractPackMultiplier(productData.name);
    return realUnits * packMultiplier;
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const data = await orderService.getOrderById(id);
        setOrderData({
          ...data,
          // Corrigir formato da data para o input
          delivery_date: formatDateForInput(data.delivery_date),
          // Ensure items have product_name for display
          items: data.order_items.map((item) => ({
            ...item,
            product_name: item.products?.name || "Produto Desconhecido",
          })),
        });
        setError("");
      } catch (err) {
        // Em caso de erro ao buscar, marcar como não encontrado
        console.error("Erro ao buscar detalhes da encomenda:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
    fetchProducts();
  }, [id]);

  // Recalcular totais automaticamente quando os itens mudarem
  useEffect(() => {
    if (orderData.items.length === 0 || products.length === 0) return;

    let newTotalCheeseCount = 0;
    let newTotalWeightKg = 0;

    orderData.items.forEach((item) => {
      const product = products.find((p) => p.id === item.product_id);

      if (product) {
        // Extrair multiplicador de pack do nome do produto
        const packMultiplier = extractPackMultiplier(product.name);

        // Calcular unidades reais considerando o multiplicador
        const realUnits = item.ordered_quantity * packMultiplier;
        newTotalCheeseCount += realUnits;

        // Peso total = unidades reais × peso unitário
        newTotalWeightKg += realUnits * getUnitWeight(product);
      }
    });

    setOrderData((prev) => ({
      ...prev,
      total_cheese_count: newTotalCheeseCount,
      total_weight_kg: newTotalWeightKg,
    }));
  }, [orderData.items, products]);

  const fetchProducts = async () => {
    try {
      const data = await productService.getProducts();
      setProducts(data);
    } catch (err) {
      setError(err.message || "Erro ao carregar produtos");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setOrderData((prev) => ({
      ...prev,
      [name]:
        type === "number"
          ? Number(value)
          : name === "delivery_date"
          ? value
          : value,
    }));
  };

  const addOrderItem = () => {
    if (!selectedProduct || selectedQuantity <= 0) {
      setError("Selecione um produto e insira uma quantidade válida");
      return;
    }

    const product = products.find((p) => p.id === selectedProduct);

    setOrderData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: selectedProduct,
          product_name: product.name, // For display only
          ordered_quantity: selectedQuantity,
        },
      ],
    }));

    // Clear selection
    setSelectedProduct("");
    setSelectedQuantity(0);
  };

  const removeOrderItem = (index) => {
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (orderData.items.length === 0) {
      setError("Adicione pelo menos um item à encomenda");
      return;
    }

    try {
      // Corrigir a data para o timezone de Portugal antes de enviar
      const orderDataWithCorrectDate = {
        ...orderData,
        delivery_date: formatDateForPortugal(orderData.delivery_date)
      };

      await orderService.updateOrder(id, orderDataWithCorrectDate);
      navigate(-1);
    } catch (err) {
      setError(err.message || "Erro ao atualizar encomenda");
    }
  };

  const handleCancelWeekly = async () => {
    if (!window.confirm(`Cancelar a recorrência semanal da encomenda ${orderData.order_number}?\n\nAs encomendas já geradas não serão afetadas, mas nenhuma nova será criada automaticamente.`)) {
      return;
    }
    try {
      setCancelingWeekly(true);
      setError("");
      await orderService.cancelWeekly(id);
      setOrderData((prev) => ({ ...prev, is_weekly: false }));
    } catch (err) {
      setError(err.message || "Erro ao cancelar recorrência semanal");
    } finally {
      setCancelingWeekly(false);
    }
  };

  // ⚠️ APENAS EM DESENVOLVIMENTO: Deletar encomenda
  const handleDelete = async () => {
    if (process.env.NODE_ENV !== 'development') {
      setError("Esta funcionalidade só está disponível em ambiente de desenvolvimento");
      return;
    }

    if (window.confirm(`Tem a certeza que deseja deletar a encomenda ${orderData.order_number || id}? Esta ação não pode ser desfeita.`)) {
      try {
        setError("");
        await orderService.deleteOrder(id);
        navigate(-1);
      } catch (err) {
        setError(err.message || "Erro ao deletar encomenda");
      }
    }
  };

  if (loading) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <p>Carregando detalhes da encomenda...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="text-center text-neutral-500">
          <p className="text-lg mb-2">Encomenda não encontrada</p>
          <p className="text-sm mb-6">A encomenda solicitada não existe ou foi removida</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-8">
        <h1 className="text-3xl font-bold text-neutral-800">
          Editar Encomenda
        </h1>
        {orderData.is_weekly && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            Semanal
          </span>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-md rounded-lg p-8"
      >
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Número da Encomenda
            </label>
            <input
              type="text"
              name="order_number"
              value={orderData.order_number}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Nome do Cliente
            </label>
            <input
              type="text"
              name="client_name"
              value={orderData.client_name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              NIF do Cliente
            </label>
            <input
              type="text"
              name="client_nif"
              value={orderData.client_nif}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Data de Entrega
            </label>
            <input
              type="date"
              name="delivery_date"
              value={orderData.delivery_date}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Quantidade Total de Queijos
            </label>
            <input
              type="number"
              name="total_cheese_count"
              value={orderData.total_cheese_count}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Peso Total (kg)
            </label>
            <input
              type="number"
              name="total_weight_kg"
              value={orderData.total_weight_kg}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Status de Entrega
            </label>
            <select
              name="delivery_status"
              value={orderData.delivery_status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              <option value="pending">Pendente</option>
              <option value="packed">Embalado</option>
              <option value="delivered">Entregue</option>
              <option value="cancelled">Cancelado</option>
              <option value="overdue">Atrasado</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-neutral-700 mb-2">
              Endereço de Destino
            </label>
            <textarea
              name="destination_address"
              value={orderData.destination_address}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Nome do Recebedor
            </label>
            <input
              type="text"
              name="recipient_name"
              value={orderData.recipient_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Detalhes da Embalagem
            </label>
            <textarea
              name="packing_details"
              value={orderData.packing_details}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400"
            />
          </div>

          <div className="md:col-span-2">
            <h2 className="text-xl font-semibold text-neutral-800 mb-4">
              Itens da Encomenda
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Produto
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                >
                  <option value="">Selecione um produto</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-neutral-700 mb-2">
                  Quantidade
                </label>
                <input
                  type="number"
                  value={selectedQuantity}
                  onChange={(e) =>
                    setSelectedQuantity(Number(e.target.value))
                  }
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={addOrderItem}
                  className="w-full md:w-auto bg-neutral-900 text-white px-4 py-2 rounded-md hover:bg-neutral-800"
                >
                  Adicionar Item
                </button>
              </div>
            </div>

            {orderData.items.length > 0 && (
              <div className="bg-neutral-100 rounded-md p-4">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-full">
                    <thead>
                      <tr className="text-left">
                        <th className="pb-2 text-sm">Produto</th>
                        <th className="pb-2 text-sm">Unidades</th>
                        <th className="pb-2 text-sm">Peso Total (kg)</th>
                        <th className="pb-2 text-right text-sm">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.items.map((item, index) => {
                        const productData = products.find(p => p.id === item.product_id);

                        // Extrair multiplicador de pack
                        const packMultiplier = productData ? extractPackMultiplier(productData.name) : 1;

                        // ordered_quantity são PACKS ou UNIDADES
                        const packsOrUnits = Number(item.ordered_quantity) || 0;

                        // Calcular unidades reais considerando o multiplicador
                        const realUnits = packsOrUnits * packMultiplier;

                        // Calcular peso total: unidades reais × peso por unidade
                        const totalWeight = realUnits * getUnitWeight(productData);

                        return (
                          <tr key={index} className="border-t border-neutral-200">
                            <td className="py-2 text-sm">{item.product_name}</td>
                            <td className="py-2 text-sm">
                              {packMultiplier > 1
                                ? `${packsOrUnits} pack${packsOrUnits !== 1 ? 's' : ''} (${realUnits} unidades)`
                                : `${realUnits} unidades`}
                            </td>
                            <td className="py-2 text-sm">{totalWeight.toFixed(2)} kg</td>
                            <td className="py-2 text-right">
                              <button
                                type="button"
                                onClick={() => removeOrderItem(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remover
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botões de ação responsivos */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto bg-neutral-100 text-neutral-900 px-4 py-2 rounded-md hover:bg-neutral-200"
          >
            Cancelar
          </button>
          {orderData.is_weekly && (
            <button
              type="button"
              onClick={handleCancelWeekly}
              disabled={cancelingWeekly}
              className="w-full sm:w-auto bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelingWeekly ? "A cancelar..." : "Cancelar Recorrência Semanal"}
            </button>
          )}
          {/* ⚠️ APENAS EM DESENVOLVIMENTO: Botão deletar */}
          {process.env.NODE_ENV === 'development' && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              🗑️ Deletar Encomenda
            </button>
          )}
          <button
            type="submit"
            className="w-full sm:w-auto bg-neutral-900 text-white px-4 py-2 rounded-md hover:bg-neutral-800"
          >
            Atualizar Encomenda
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderDetails;
